import type { FC } from "react";
import { useState } from "react";

import { Arrange } from "@aviary";
import { logGaSignupEvent } from "@shared/helpers";
import { hasErrors } from "@shared/helpers/hasErrors";
import { useBreakpoints, useSharedGlobalConfig } from "@shared/hooks";
import { useSearchParams } from "@shared/react-router-dom";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";
import { Platforms } from "@shared/types/platforms";
import { gRecaptchaExecute } from "@shared/utils";
import { usePractitionerSignUpMutation } from "@unauthenticated/shared/data/mutations";
import type { PractitionerSignUpMutationData } from "@unauthenticated/shared/data/mutations/PractitionerSignUp.mutation";
import { useLogPractitionerSignUp } from "@unauthenticated/shared/hooks";

import { SignUpCarouselOrFooter } from "../SignUpCarouselOrFooter";

import { SignUpPageSelector } from "./SignUpPageSelector";
import type { SignUpPageType, SimplifySignUpFormData } from "./types";

interface SimplifySignUpProps {
  additionalAttributes?: PractitionerAdditionalAttributesType;
  csuid: string;
  isOAuth?: boolean;
  onCompleted: (data: PractitionerSignUpMutationData) => void;
  newSSOErrors?: string;
}

const SimplifySignUp: FC<SimplifySignUpProps> = ({
  additionalAttributes,
  csuid,
  isOAuth,
  onCompleted,
  newSSOErrors,
}) => {
  const { isEmerson } = useSharedGlobalConfig();

  const platform = isEmerson ? "Emerson" : Platforms.FULLSCRIPT;
  const { tablet } = useBreakpoints();
  const pageVersion = "signup_with_t&c";
  const [searchParams, setSearchParams] = useSearchParams();
  const { referredFirstName, referredLastName } =
    additionalAttributes?.peerReferralDisplayAttributes || {};

  const [formData, setFormData] = useState<SimplifySignUpFormData>({
    firstName: referredFirstName ?? "",
    lastName: referredLastName ?? "",
    dispensaryName: "",
    practitionerType: null,
    tosTimestamp: "",
    email: "",
    password: "",
    platform,
  });

  const [isAccountExists, setIsAccountExists] = useState(false);

  const signUpPage: SignUpPageType = isOAuth ? "oauth_sign_up_page" : "practitioner_sign_up_page";
  const { logField } = useLogPractitionerSignUp({
    eventAggregateId: csuid,
    pageVersion,
    signUpPage,
    apiClientId: additionalAttributes?.apiClientId,
  });

  const isPageOneErrors = fields => {
    return fields?.firstName || fields?.lastName || fields?.practitionerTypeId;
  };

  const isPageTwoErrors = fields => {
    return fields?.email;
  };

  const [signUp, { loading, data }] = usePractitionerSignUpMutation({
    onCompleted: payload => {
      if (!hasErrors(payload?.auth?.practitionerSignUp?.errors)) {
        const { id, storeId } = payload?.auth?.practitionerSignUp?.practitioner || {};
        logGaSignupEvent(id, storeId);
      } else if (isPageOneErrors(payload?.auth?.practitionerSignUp?.errors?.fields)) {
        searchParams.set("step", "1");
        setSearchParams(searchParams);
      } else if (isPageTwoErrors(payload?.auth?.practitionerSignUp?.errors?.fields)) {
        // currently the only page two error from BE is duplicate email
        setIsAccountExists(true);
      }
      onCompleted(payload);
    },
  });

  const handleSubmit = () => {
    const { firstName, lastName, email, password, practitionerType, dispensaryName } = formData;

    const relevantAdditionalAttributesForSignUp = { ...additionalAttributes };

    delete relevantAdditionalAttributesForSignUp?.peerReferralDisplayAttributes;

    gRecaptchaExecute(gToken => {
      signUp({
        variables: {
          input: {
            attributes: {
              firstName,
              lastName,
              storeName: dispensaryName,
              email,
              password,
              practitionerTypeId: practitionerType.id,
              pageVersion,
              signUpPage,
              optional: relevantAdditionalAttributesForSignUp,
              platform,
            },
            captchaToken: gToken,
          },
        },
      });
    });
  };

  return (
    <Arrange
      justify="spaceBetween"
      alignment="center"
      isVertical
      isFullHeight
      data-testid="simplify-signup"
    >
      <SignUpPageSelector
        errors={data?.auth?.practitionerSignUp?.errors?.fields}
        isLoading={loading}
        formData={formData}
        setFormData={setFormData}
        logField={logField}
        onSubmit={handleSubmit}
        isAccountExists={isAccountExists}
        setIsAccountExists={setIsAccountExists}
        additionalAttributes={additionalAttributes}
        pageVersion={pageVersion}
        signUpPage={signUpPage}
        newSSOErrors={newSSOErrors}
        isEmerson={isEmerson}
      />
      {!tablet.greaterThan && (
        <SignUpCarouselOrFooter isWithinViewport={tablet.greaterThan} hideFooterSignIn />
      )}
    </Arrange>
  );
};

export { SimplifySignUp };
